import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { Button, Card, Field, Input, useToast } from '../../components/ui.js';
import { ZestMark } from '../../components/ZestMark.js';
import './auth.css';

/**
 * Public waiting-list form (linked from the marketing hero). Collects name,
 * company, phone and email; the company domain is derived server-side from
 * the email ending. Submissions are rate-limited to 2 per 5 minutes per IP,
 * and a hidden honeypot field silently discards bot submissions.
 */
export function WaitlistPage() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', companyName: '', phone: '', email: '' });
  // Honeypot — humans never see it; bots fill it.
  const [website, setWebsite] = useState('');
  const [done, setDone] = useState(false);

  const domain = form.email.includes('@') ? form.email.split('@')[1]?.trim().toLowerCase() : '';

  const submit = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: true; message: string }>('/waitlist', {
        method: 'POST',
        body: { ...form, website },
      }),
    onSuccess: () => setDone(true),
    onError: (err) => {
      if (err instanceof ApiRequestError && err.code === 'rate_limited') {
        toast('Too many submissions — please wait a few minutes and try again.', 'error');
        return;
      }
      toast(err instanceof ApiRequestError ? err.message : 'Something went wrong — please try again.', 'error');
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit.mutate();
  };

  return (
    <div className="auth-viewport">
      <Card className="auth-card auth-card-wide">
        <div className="auth-brand">
          <ZestMark className="auth-logo" />
          <h1>Join the waiting list</h1>
        </div>

        {done ? (
          <>
            <p className="auth-tagline">
              You&rsquo;re on the list. We review every organisation by hand — as soon as{' '}
              {domain ? <strong>{domain}</strong> : 'your company'} is approved, we&rsquo;ll email
              you and everyone with a matching address will be able to sign in with Microsoft 365
              or Google Workspace.
            </p>
            <p className="auth-footer-note">
              <Link to="/login">Back to sign in</Link>
            </p>
          </>
        ) : (
          <>
            <p className="auth-tagline">
              Zest Assure is invite-only while we onboard organisations. Tell us who you are and
              we&rsquo;ll email you the moment your company is approved.
            </p>

            <form onSubmit={onSubmit} aria-label="Join the waiting list">
              <Field label="Your name" required>
                {(props) => (
                  <Input
                    {...props}
                    required
                    maxLength={200}
                    autoComplete="name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                )}
              </Field>
              <Field label="Company name" required>
                {(props) => (
                  <Input
                    {...props}
                    required
                    maxLength={200}
                    autoComplete="organization"
                    value={form.companyName}
                    onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
                  />
                )}
              </Field>
              <Field label="Phone number">
                {(props) => (
                  <Input
                    {...props}
                    type="tel"
                    maxLength={50}
                    autoComplete="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                )}
              </Field>
              <Field
                label="Work email address"
                required
                hint={
                  domain
                    ? `We'll approve access for everyone @${domain}`
                    : 'Use your company address — access is approved per company domain'
                }
              >
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    required
                    maxLength={320}
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  />
                )}
              </Field>

              {/* Honeypot: visually hidden, skipped by keyboard and readers. */}
              <div className="sr-only" aria-hidden="true">
                <label>
                  Website
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </label>
              </div>

              <Button type="submit" className="auth-block-btn" loading={submit.isPending}>
                Request access
              </Button>
            </form>

            <p className="auth-footer-note">
              Already approved? <Link to="/login">Sign in</Link>
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
